import { useEffect, useState } from "react";
import { format, differenceInMinutes } from "date-fns";

export default function TimesheetRow({
  onSelect,
  schedules,
  selectedScheduleID,
}) {
  useEffect(() => {
    if (schedules.length > 0) {
      // let firstScheduleID = schedules[0]?._id;
      // onSelect(firstScheduleID);
    }
  }, [schedules]);

  const [sortConfig, setSortConfig] = useState({
    key: "date",
    direction: "asc",
  });

  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const sortedSchedules = [...schedules].sort((a, b) => {
    if (sortConfig.key === "date") {
      return sortConfig.direction === "asc"
        ? new Date(a.startTime) - new Date(b.startTime)
        : new Date(b.startTime) - new Date(a.startTime);
    }
    if (sortConfig.key === "status") {
      return sortConfig.direction === "asc"
        ? a.status.localeCompare(b.status)
        : b.status.localeCompare(a.status);
    }
    return 0;
  });

  const timeFormat = (timeStr) => {
    const time = new Date(timeStr);
    time.setFullYear(time.getUTCFullYear());
    time.setMonth(time.getUTCMonth());
    time.setDate(time.getUTCDate());
    time.setHours(time.getUTCHours());
    time.setMinutes(time.getUTCMinutes());
    time.setSeconds(time.getUTCSeconds());
    return time;
  };

  const calcDuration = (startISO, endISO) => {
    if (!startISO || !endISO) return "";

    const start = new Date(startISO);
    const end = new Date(endISO);

    const diffMs = end - start; // difference in milliseconds
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

    return `${diffHours}h ${diffMinutes}m`;
  };

  return (
    <div className="w-full max-h-[300px] min-h-[100px] border-b border-gray-200 mb-8 h-fit overflow-y-auto">
      {/* Header Row */}
      <div className="flex items-center text-[12px] bg-gray-50 sticky top-0 z-10">
        <div
          className="w-24 py-3 font-medium text-gray-600 cursor-pointer pl-4  hover:bg-gray-100"
          onClick={() => requestSort("date")}
        >
          Date{" "}
          {sortConfig.key === "date" && (
            <span className="ml-1">
              {sortConfig.direction === "asc" ? "↑" : "↓"}
            </span>
          )}
        </div>
        <div className="w-8 mr-4 py-3 font-medium text-gray-600"></div>

        <div
          className="w-36 py-3 font-medium text-gray-600 cursor-pointer hover:bg-gray-100"
          onClick={() => requestSort("status")}
        >
          Status{" "}
          {sortConfig.key === "status" && (
            <span className="ml-1">
              {sortConfig.direction === "asc" ? "↑" : "↓"}
            </span>
          )}
        </div>
        <div className="w-16 mr-4 py-3 font-medium text-gray-600">Area</div>
        <div className="w-56 mr-4 py-3 font-medium text-gray-600">Location</div>
        <div className="w-56 py-3 font-medium text-gray-600 mr-8">Time</div>
        <div className="w-24 py-3 font-medium text-gray-600">Total Hours</div>
        <div className="py-3 font-medium text-gray-600">Paid</div>
      </div>

      {/* Data Rows */}
      {sortedSchedules.map((item, index) => {
        const minutesDiff = differenceInMinutes(
          new Date(item.endTime),
          new Date(item.startTime)
        );
        const hours = Math.floor(Math.abs(minutesDiff) / 60);
        const minutes = minutesDiff % 60;
        const durationString =
          minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;

        return (
          <div
            key={index}
            onClick={() => onSelect(item._id)}
            className={`flex items-center cursor-pointer text-[12px] border-b ${
              selectedScheduleID == item._id ? "bg-gray-100" : ""
            }`}
          >
            <span className="text-gray-700 w-24  pl-4">
              {format(new Date(item.startTime), "EEE dd/MMM")}
            </span>
            <span className="text-gray-700 w-8  mr-4">
              {item?.timesheetID?.publicHoliday && "🎁"}
            </span>
            <div className="text-yellow-600 font-bold w-36">
              <StatusTag payAmount={item.payAmount} status={item.status} />
            </div>
            <span className="text-gray-700 underline cursor-pointer mr-4 w-16">
              {item.locationID?.area}
            </span>
            <span className="text-gray-700 underline cursor-pointer mr-4 w-56">
              {item.locationID?.name}
            </span>
            <div className="text-[12px] w-56 text-gray-400 font-semibold mr-8">
              <span>{format(timeFormat(item.startTime), "hh:mm aa")}</span>{" "}
              {` - `}
              <span>{format(timeFormat(item.endTime), "hh:mm aa")}</span>
            </div>
            <span className="w-24 text-gray-500 font-semibold">
              Total:{" "}
              {item.approved
                ? calcDuration(
                    item.timesheetID.startTime,
                    item.timesheetID.endTime
                  )
                : item.status == "pending"
                ? calcDuration(item.clockIn, item.clockOut)
                : durationString}
            </span>
            <span>
              {item?.timesheetID?.payAmount &&
                `€ ${item?.timesheetID?.payAmount}`}
            </span>
          </div>
        );
      })}
    </div>
  );
}

// Keep your existing StatusTag component and statusColors object

const statusColors = {
  created: {
    background: "bg-sky-100", // Tailwind sky blue 200
    text: "text-sky-800",
  },
  started: {
    background: "bg-red-100", // Tailwind sky 200
    text: "text-red-800",
  },
  pending: {
    background: "bg-orange-300", // Tailwind orange 300 (deep orange)
    text: "text-orange-900",
  },
  approved: {
    background: "bg-green-700", // Tailwind green 200
    text: "text-white",
  },
};

function StatusTag({ status, payAmount }) {
  const lowerStatus = status.toLowerCase();
  const colors = statusColors[lowerStatus] || {
    background: "bg-gray-200",
    text: "text-gray-700",
  };

  return (
    <span
      className={`inline-block px-3 py-0.5 m-0.5 rounded-md font-light ${colors.background} ${colors.text} select-none`}
      aria-label={`Status: ${status}`}
      role="status"
    >
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
}
